/**
 * Axios 封装与统一拦截器。
 *
 * 职责：
 * 1. 创建 axios 实例（baseURL 来自 `VITE_API_BASE_URL`，超时 30s）。
 * 2. 请求拦截器：注入 `Authorization: Bearer` 与可选 `X-Trace-Id`。
 * 3. 响应拦截器：解包统一响应 `{code,message,data}`；`code!==0` 抛出 `Error(message)`；
 *    遇到 401 时尝试通过 authStore 刷新令牌并重试一次，刷新失败则清除会话并跳转登录。
 *
 * 注意：本模块仅依赖 `authStore`（store 层）与 `config/api`，不反向依赖业务 service，
 * 以避免循环引用。
 *
 * @module services/http
 */
import axios, {
  type AxiosError,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import { API_BASE_URL } from '@/config/api';
import { useAuthStore } from '@/store/authStore';
import type { ApiResponse } from '@/types';

/** localStorage 会话键（与 authStore 保持一致）。 */
const TOKEN_KEY = 'relay_token';
const USER_KEY = 'relay_user';
const REFRESH_KEY = 'relay_refresh';

/** 生成简单 traceId（用于链路追踪，可选）。 */
function generateTraceId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `t-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/** 安全读取 localStorage 中的访问令牌。 */
function readToken(): string | null {
  try {
    return window.localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

/** 判断是否为认证相关端点（不参与 401 刷新重试，避免递归）。 */
function isAuthEndpoint(url: string): boolean {
  return url.includes('/auth/');
}

/** 判断是否为后端统一包装响应。 */
function isApiResponse(value: unknown): value is ApiResponse<unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    'code' in value &&
    'data' in value
  );
}

/** 认证失败：清除会话并跳转登录页。 */
function handleAuthFailure(): void {
  useAuthStore.getState().logout();
  if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
    window.location.assign('/login');
  }
}

/** 创建 axios 实例。 */
const http = axios.create({
  baseURL: API_BASE_URL || undefined,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

/** 请求拦截器：注入令牌与 traceId。 */
http.interceptors.request.use(
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    const token = readToken();
    if (token) {
      config.headers.set('Authorization', `Bearer ${token}`);
    }
    config.headers.set('X-Trace-Id', generateTraceId());
    return config;
  },
  (error: AxiosError): Promise<AxiosError> => Promise.reject(error),
);

/** 响应拦截器：解包响应 / 处理 401 刷新重试。 */
http.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => {
    const body = response.data;
    if (isApiResponse(body)) {
      if (body.code !== 0) {
        return Promise.reject(new Error(body.message || '请求失败')) as never;
      }
      // 解包为业务 data（运行时值已是 T，此处仅作类型对齐）。
      return body.data as unknown as AxiosResponse;
    }
    // 非标准包装：直接返回原始 data。
    return response.data as unknown as AxiosResponse;
  },
  async (error: AxiosError): Promise<unknown> => {
    const status = error.response?.status;
    const original = error.config as
      | (InternalAxiosRequestConfig & { _retry?: boolean })
      | undefined;
    const url = original?.url ?? '';

    if (
      status === 401 &&
      original &&
      !original._retry &&
      !isAuthEndpoint(url)
    ) {
      original._retry = true;
      try {
        await useAuthStore.getState().refresh();
        const newToken = useAuthStore.getState().token;
        if (newToken) {
          original.headers.set('Authorization', `Bearer ${newToken}`);
        }
        return http(original);
      } catch {
        handleAuthFailure();
        return Promise.reject(new Error('登录已过期，请重新登录'));
      }
    }

    // 认证端点失败或刷新失败：清理会话。
    if (status === 401) {
      handleAuthFailure();
    }

    const data = error.response?.data as ApiResponse<unknown> | undefined;
    const message =
      data && isApiResponse(data) && data.message
        ? data.message
        : error.message || '网络请求失败';
    return Promise.reject(new Error(message));
  },
);

/**
 * 发起请求并自动解包 `{code,message,data}`。
 *
 * @typeParam T 解包后的业务数据类型。
 * @param config axios 请求配置。
 * @returns 解包后的 `data`。
 */
export async function request<T>(config: AxiosRequestConfig): Promise<T> {
  const result = await http.request<ApiResponse<T>>(config);
  return result as unknown as T;
}

export { http };
export default http;
